import { BaseCanvasPack } from "./BaseCanvasPack";

export default class AlienRuinCanvasPack extends BaseCanvasPack {
  private glyphCanvas: HTMLCanvasElement | null = null;
  private readonly GLYPH_SIZE = 48; // Increased to accommodate the glow/shadow without clipping
  private readonly GLYPH_DRAW_SIZE = 20; // The actual size of the glyph strokes
  private readonly GLYPH_COUNT = 64;

  drawBackground(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number,
    level?: number
  ): void {
    // Dark, sickly greens and purples
    const gradient = context.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#1a0b2e"); // Dark purple
    gradient.addColorStop(0.5, "#001a00"); // Dark green
    gradient.addColorStop(1, "#1a0b2e"); // Dark purple

    context.fillStyle = gradient;
    context.fillRect(0, 0, width, height);

    if (frameTime !== undefined) {
      this.drawUndulatingTextures(context, width, height, frameTime);
      this.drawMist(context, width, height, frameTime);
    }
  }

  drawFrame(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    const borderWidth = 4;
    
    // Base frame - dark organic material
    context.strokeStyle = "#2e1a47"; // Dark purple organic matter
    context.lineWidth = borderWidth;
    context.strokeRect(borderWidth/2, borderWidth/2, width - borderWidth, height - borderWidth);

    // Glow-in-the-dark glyphs
    if (frameTime !== undefined) {
        this.initGlyphs();

        const pulse = (Math.sin(frameTime * 0.003) + 1) * 0.5; // 0 to 1
        const baseAlpha = 0.3 + pulse * 0.7;
        
        // PERFORMANCE FIX: Removed real-time shadowBlur. 
        // The glow is now baked into the glyph atlas.
        
        // Draw glyphs along the border
        // Top
        this.drawGlyphs(context, borderWidth, borderWidth/2, width - borderWidth, borderWidth/2, frameTime, 1, baseAlpha);
        // Bottom
        this.drawGlyphs(context, borderWidth, height - borderWidth/2, width - borderWidth, height - borderWidth/2, frameTime, 2, baseAlpha);
        // Left
        this.drawGlyphs(context, borderWidth/2, borderWidth, borderWidth/2, height - borderWidth, frameTime, 3, baseAlpha);
        // Right
        this.drawGlyphs(context, width - borderWidth/2, borderWidth, width - borderWidth/2, height - borderWidth, frameTime, 4, baseAlpha);
    }
  }

  private initGlyphs() {
    if (this.glyphCanvas) return;
    
    this.glyphCanvas = document.createElement('canvas');
    this.glyphCanvas.width = this.GLYPH_SIZE * this.GLYPH_COUNT;
    this.glyphCanvas.height = this.GLYPH_SIZE;
    
    const ctx = this.glyphCanvas.getContext('2d');
    if (!ctx) return;
    
    // Bake the shadow/glow into the texture
    ctx.shadowBlur = 10;
    ctx.shadowColor = "#00ff00";
    
    ctx.strokeStyle = "rgb(50, 255, 50)";
    ctx.fillStyle = "rgb(50, 255, 50)";
    ctx.lineWidth = 2; // Slightly thicker to stand out with the glow
    ctx.lineCap = "round";
    
    for (let i = 0; i < this.GLYPH_COUNT; i++) {
        ctx.save();
        ctx.translate(i * this.GLYPH_SIZE + this.GLYPH_SIZE/2, this.GLYPH_SIZE/2);
        this.drawAlienChar(ctx, i * 137, this.GLYPH_DRAW_SIZE); 
        ctx.restore();
    }
  }

  private drawGlyphs(
      context: CanvasRenderingContext2D, 
      x1: number, y1: number, x2: number, y2: number, 
      frameTime: number, 
      sideSeed: number,
      baseAlpha: number
  ) {
      if (!this.glyphCanvas) return;

      const dx = x2 - x1;
      const dy = y2 - y1;
      const length = Math.sqrt(dx*dx + dy*dy);
      const spacing = 20;
      const count = Math.floor(length / spacing);
      
      const angle = Math.atan2(dy, dx);

      for (let i = 0; i < count; i++) {
          const t = i / (count - 1 || 1);
          const x = x1 + dx * t;
          const y = y1 + dy * t;
          
          // Stable seed for this specific glyph based on position
          const glyphSeed = sideSeed * 1000 + i;
          
          // Flicker effect
          const flickerSpeed = 0.005;
          const flicker = Math.sin(frameTime * flickerSpeed + glyphSeed) * Math.sin(frameTime * flickerSpeed * 2.3 + glyphSeed * 1.1);
          
          // Randomly hide some glyphs or make them dim
          if (flicker < -0.3) continue;
          
          const flickerAlpha = 0.3 + (flicker + 0.5) * 0.5; // 0.3 to ~1.0
          context.globalAlpha = baseAlpha * flickerAlpha;

          context.save();
          context.translate(x, y);
          context.rotate(angle);
          
          // Pick a glyph from the atlas
          const glyphIndex = Math.abs(Math.floor(Math.sin(glyphSeed) * 10000)) % this.GLYPH_COUNT;
          
          context.drawImage(
            this.glyphCanvas, 
            glyphIndex * this.GLYPH_SIZE, 0, this.GLYPH_SIZE, this.GLYPH_SIZE,
            -this.GLYPH_SIZE/2, -this.GLYPH_SIZE/2, this.GLYPH_SIZE, this.GLYPH_SIZE
          );
          
          context.restore();
      }
      context.globalAlpha = 1.0;
  }

  private drawAlienChar(context: CanvasRenderingContext2D, seed: number, size: number) {
      // Procedural alien character
      // Use seed to determine strokes
      // Simple pseudo-random generator
      const rand = (s: number) => {
          return Math.abs((Math.sin(s) * 10000) % 1);
      };

      const numStrokes = 2 + Math.floor(rand(seed) * 3); // 2 to 4 strokes
      
      context.beginPath();
      for (let k = 0; k < numStrokes; k++) {
          const s = seed + k * 17;
          // Points in a -0.5 to 0.5 range scaled by size
          const p1x = (rand(s) - 0.5) * size;
          const p1y = (rand(s + 1) - 0.5) * size;
          const p2x = (rand(s + 2) - 0.5) * size;
          const p2y = (rand(s + 3) - 0.5) * size;
          
          context.moveTo(p1x, p1y);
          context.lineTo(p2x, p2y);
      }
      context.stroke();
      
      // Occasional dot
      if (rand(seed + 10) > 0.7) {
          context.beginPath();
          const dx = (rand(seed + 11) - 0.5) * size;
          const dy = (rand(seed + 12) - 0.5) * size;
          context.arc(dx, dy, 1.5, 0, Math.PI*2);
          context.fill();
      }
  }

  drawOverlay(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime?: number
  ): void {
    // None
  }

  private drawUndulatingTextures(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number
  ): void {
    context.save();
    context.globalCompositeOperation = "screen"; // Additive blending for glow
    
    // Draw organic, morphing shapes
    const shapeCount = 3;
    
    for (let i = 0; i < shapeCount; i++) {
        const t = frameTime * 0.0002 + i * 100;
        
        // Center position moves slowly
        const cx = width/2 + Math.sin(t * 0.5) * (width * 0.2);
        const cy = height/2 + Math.cos(t * 0.3) * (height * 0.2);
        
        const radius = Math.min(width, height) * (0.3 + Math.sin(t) * 0.1);
        
        context.beginPath();
        
        // Draw a blob using bezier curves
        const points = 8;
        const angleStep = (Math.PI * 2) / points;
        
        for (let j = 0; j <= points; j++) {
            const angle = j * angleStep;
            // Morphing radius for each point
            const r = radius + Math.sin(t * 2 + j * 3) * 30 + Math.cos(t * 3 + j) * 20;
            
            const x = cx + Math.cos(angle) * r;
            const y = cy + Math.sin(angle) * r;
            
            if (j === 0) {
                context.moveTo(x, y);
            } else {
                // Simple control points for smooth curve (could be improved)
                const prevAngle = (j - 1) * angleStep;
                const prevR = radius + Math.sin(t * 2 + (j - 1) * 3) * 30 + Math.cos(t * 3 + (j - 1)) * 20;
                const prevX = cx + Math.cos(prevAngle) * prevR;
                const prevY = cy + Math.sin(prevAngle) * prevR;
                
                const cp1x = prevX + (x - prevX) * 0.3 - (y - prevY) * 0.1;
                const cp1y = prevY + (y - prevY) * 0.3 + (x - prevX) * 0.1;
                const cp2x = x - (x - prevX) * 0.3 + (y - prevY) * 0.1;
                const cp2y = y - (y - prevY) * 0.3 - (x - prevX) * 0.1;
                
                context.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);
            }
        }
        
        context.closePath();
        
        // Gradient fill
        const gradient = context.createRadialGradient(cx, cy, 0, cx, cy, radius * 1.5);
        gradient.addColorStop(0, `rgba(0, 100, 50, ${0.1 + Math.sin(t)*0.05})`);
        gradient.addColorStop(1, "rgba(0, 0, 0, 0)");
        
        context.fillStyle = gradient;
        context.fill();
    }
    
    context.restore();
  }

  private drawMist(
    context: CanvasRenderingContext2D,
    width: number,
    height: number,
    frameTime: number
  ): void {
    context.save();
    
    // Two layers of mist moving in opposite directions
    this.drawMistLayer(context, width, height, frameTime, 0.02, 0.3, 0.1);
    this.drawMistLayer(context, width, height, frameTime + 5000, -0.015, 0.2, 0.05);
    
    context.restore();
  }

  private drawMistLayer(
      context: CanvasRenderingContext2D,
      width: number,
      height: number,
      frameTime: number,
      speed: number,
      baseHeightRatio: number,
      opacity: number
  ) {
      context.fillStyle = `rgba(20, 255, 50, ${opacity})`;
      
      context.beginPath();
      context.moveTo(0, height);
      
      const waveCount = 5;
      const step = width / waveCount;
      
      // Draw wavy top of mist
      for (let x = 0; x <= width + step; x += step/2) {
          // Calculate y based on sine waves
          const t = frameTime * 0.001;
          const waveHeight = Math.sin(x * 0.01 + t * speed * 100) * 20 + Math.cos(x * 0.02 - t * speed * 50) * 15;
          const y = height - (height * baseHeightRatio) + waveHeight;
          
          if (x === 0) context.lineTo(x, y);
          else context.lineTo(x, y);
      }
      
      context.lineTo(width, height);
      context.lineTo(0, height);
      context.fill();
  }

  getBorderStyle(): string {
    return "4px solid #2e1a47";
  }

  getBoxShadow(frameTime?: number): string {
    let pulse = 0.5;
    if (frameTime !== undefined) {
        pulse = 0.5 + 0.3 * Math.sin(frameTime * 0.003);
    }
    return `0 0 20px rgba(0, 255, 0, ${pulse * 0.5})`;
  }

  getBackdropFilter(): string {
    return "blur(5px) hue-rotate(90deg)";
  }
}
